import { useEffect } from "react"
import { useDispatch, useSelector } from "react-redux"
import { FiInfo } from "react-icons/fi"

import { Title, Container, Number, Checkbox, Select } from "components/Form"
import { limsSstTemplates } from "state_management"
import {
  SST_INSTRUMENT_TYPES,
  instrumentTypeLabelKey,
  selectByInstrumentType,
} from "state_management/lims/limsSstTemplates"
import useT from "utils/hooks/useT"

// SystemSuitability — edits the System Suitability Test specification of an Analysis definition
// (PRD-005 Area 3 / SPEC-017): whether the analysis REQUIRES an SST before results can be sent to
// review, and the SST acceptance criteria (RSD-area ≤, RSD-RT ≤, tailing interval, plate count N ≥,
// resolution Rs ≥, and the expected injection count). Shown only when the LIMS module is on,
// mirroring the Review-Specification section.
//
// It is a controlled component: the parent (AnalysisConfigurationPage) owns the `spec` object and
// persists it via the limsSstSpec slice on save (keyed by the analysis-definition id). The criteria
// fields are always editable; an unset (blank) criterion simply is not judged for that parameter.
//
// Layout (PRD-006 K20): the section heading lives INSIDE the box; the "requires SST" checkbox sits
// side by side with its label at a larger size; the grey criterion labels share ONE fixed column
// width so every row aligns; the explanation comes last via the shared info-icon convention. The
// shared `.LimsConfigBox-*` classes (defined in index.scss) are reused from ReviewSpecification —
// the single SCSS convention for the LIMS analysis-definition config boxes.
const LABEL_WIDTH = "16rem"

export default function SystemSuitability({ spec, onChange }) {
  const { t } = useT("common")
  const dispatch = useDispatch()
  const set = (field, value) => onChange({ ...spec, [field]: value })

  // F4 (SPEC-036) — the live shared SST templates for the chosen instrument type, offered as the
  // optional DEFAULT template the run pre-selects. Load the library once so the picker can populate.
  const instrumentType = spec.instrumentType || ""
  const templates = useSelector((state) => selectByInstrumentType(state, instrumentType))
  useEffect(() => {
    dispatch(limsSstTemplates.fetch())
  }, [dispatch])

  // Changing the instrument type clears the default template (it belonged to the previous type);
  // re-selecting the same type is a no-op that keeps it. Both fields update in one call so the
  // controlled spec never holds a default template that doesn't match its instrument type.
  const setInstrumentType = (value) => {
    const next = value || null
    const keepDefault = next === instrumentType ? spec.defaultSstTemplateId : null
    onChange({ ...spec, instrumentType: next, defaultSstTemplateId: keepDefault })
  }

  return (
    <div className="container-compartment AnalysisConfigurationPage-lims-compartment LimsConfigBox">
      <h4 className="LimsConfigBox-heading">{t("limsSstSection")}</h4>

      <div className="LimsConfigBox-fields">
        <label className="LimsConfigBox-checkbox">
          <Checkbox checked={!!spec.requiresSst} onChange={(v) => set("requiresSst", v)} />
          <span className="LimsConfigBox-checkbox-label">{t("limsSstRequires")}</span>
        </label>

        {/* F4 — instrument type drives the run-time SST template picker; the default template
            (filtered to that type) pre-selects one for the analyst. Both optional: with neither
            set, the inline criteria below apply (back-compatible). */}
        <Container>
          <Title width={LABEL_WIDTH}>{t("limsSstInstrumentType")}</Title>
          <Select
            isMulti={false}
            includeNone
            noneLabel={t("limsSstInstrumentNone")}
            collection={SST_INSTRUMENT_TYPES.map((type) => ({ id: type, name: t(instrumentTypeLabelKey(type)) }))}
            value={instrumentType}
            onChange={(v) => setInstrumentType(v && v[0])}
            menuPlacement="bottom"
          />
        </Container>
        {instrumentType && templates.length > 0 && (
          <Container>
            <Title width={LABEL_WIDTH}>{t("limsSstDefaultTemplate")}</Title>
            <Select
              isMulti={false}
              includeNone
              noneLabel={t("limsSstTemplateInline")}
              collection={templates.map((tpl) => ({ id: tpl.id, name: `${tpl.name} (v${tpl.versionNumber})` }))}
              value={spec.defaultSstTemplateId || ""}
              onChange={(v) => set("defaultSstTemplateId", (v && v[0]) || null)}
              menuPlacement="bottom"
            />
          </Container>
        )}

        <Container>
          <Title width={LABEL_WIDTH}>{t("limsSstRsdAreaMax")}</Title>
          <Number min={0} value={spec.rsdAreaMax} onChange={(v) => set("rsdAreaMax", v)} />
        </Container>
        <Container>
          <Title width={LABEL_WIDTH}>{t("limsSstRsdRtMax")}</Title>
          <Number min={0} value={spec.rsdRtMax} onChange={(v) => set("rsdRtMax", v)} />
        </Container>
        <Container>
          <Title width={LABEL_WIDTH}>{t("limsSstTailingMin")}</Title>
          <Number min={0} value={spec.tailingMin} onChange={(v) => set("tailingMin", v)} />
        </Container>
        <Container>
          <Title width={LABEL_WIDTH}>{t("limsSstTailingMax")}</Title>
          <Number min={0} value={spec.tailingMax} onChange={(v) => set("tailingMax", v)} />
        </Container>
        <Container>
          <Title width={LABEL_WIDTH}>{t("limsSstPlateCountMin")}</Title>
          <Number min={0} value={spec.plateCountMin} onChange={(v) => set("plateCountMin", v)} />
        </Container>
        <Container>
          <Title width={LABEL_WIDTH}>{t("limsSstResolutionMin")}</Title>
          <Number min={0} value={spec.resolutionMin} onChange={(v) => set("resolutionMin", v)} />
        </Container>
        <Container>
          <Title width={LABEL_WIDTH}>{t("limsSstInjectionCount")}</Title>
          <Number min={1} value={spec.injectionCount} onChange={(v) => set("injectionCount", v)} />
        </Container>
      </div>

      <p className="LimsConfigBox-help">
        <FiInfo className="LimsConfigBox-help-icon" title={t("limsSstRequiresHelp")} />
        <span>{t("limsSstRequiresHelp")}</span>
      </p>
    </div>
  )
}
